import { Badge, fmt, type Column } from "@nabble/ui";
import type { OrganizationMember } from "../../../api/organizations";
import { RoleChips } from "../../../components/RoleChips";
import { roleNames } from "../../../model/organizationRoles";
import { STATUS_LABEL, STATUS_TONE } from "../../../model/userStatus";

export interface MemberRowHandlers {
  editRoles: (member: OrganizationMember) => void;
  remove: (member: OrganizationMember) => void;
}

function MemberCell({ member: m }: { member: OrganizationMember }): React.JSX.Element {
  return (
    <div className="nb-cell-main">
      <span className="nb-avatar">{fmt.initials(m.displayName || m.email)}</span>
      <div className="nb-cell-stack">
        <strong title={m.displayName}>
          {m.displayName}
          {m.self ? (
            <>
              {" "}
              <Badge label="You" tone="info" dot={false} />
            </>
          ) : null}
        </strong>
        <span className="nb-cell-sub" title={m.email}>
          {m.email}
        </span>
      </div>
    </div>
  );
}

/** The columns of an organization's people; nobody can change their own roles or remove themselves. */
export function memberColumns({ editRoles, remove }: MemberRowHandlers): Column<OrganizationMember>[] {
  return [
    {
      key: "displayName",
      label: "Person",
      minWidth: 260,
      text: (m) => `${m.displayName} ${m.email}`,
      render: (m) => <MemberCell member={m} />,
    },
    {
      key: "roles",
      label: "Roles",
      minWidth: 220,
      sortable: false,
      wrap: true,
      text: (m) => roleNames(m.roles),
      render: (m) => <RoleChips roles={m.roles} />,
    },
    {
      key: "status",
      label: "Status",
      type: "badge",
      width: 140,
      badge: (m) => ({ label: STATUS_LABEL[m.status], tone: STATUS_TONE[m.status] }),
    },
    { key: "joinedAt", label: "Joined", type: "datetime", width: 150, text: (m) => fmt.date(m.joinedAt) },
    {
      key: "actions",
      label: "Actions",
      type: "actions",
      width: 60,
      actions: (m) => [
        { label: "Edit roles", icon: "key", onSelect: () => editRoles(m), disabled: m.self },
        { label: "Remove from the organization", icon: "userX", tone: "danger", onSelect: () => remove(m), disabled: m.self },
      ],
    },
  ];
}
